
import {
  defineComponent,
  SetupContext,
  ComponentPublicInstance,
  ref,
  onMounted,
  nextTick,
  computed,
  PropType
} from "vue";
import DetailImageView from "./DetailImageView.vue";
import DetailVideoView from "./DetailVideoView.vue";
import DetailTitleView from "./DetailTitleView.vue";
import DetailIngredientsView from "./DetailIngredientsView.vue";
import DetailInstructionsView from "./DetailInstructionsView.vue";
import DetailFooterView from "./DetailFooterView.vue";
import DetailMyApplianceView from "./DetailMyApplianceView.vue";
import { RecipeDetailInfo, InstructionItem, Attachment, Variation, Theme } from "@/types/recipes";
import NativeClient from "@/client/native-client";
import { ApplianceData, AttachmentData, RecipeComment } from "@/types/common";
import { SEARCH_APPLIANCE_CATEGORY_ID } from "@/types/search";

export default defineComponent({
  name: "RecipeDetailView",
  components: {
    DetailImageView,
    DetailVideoView,
    DetailTitleView,
    DetailIngredientsView,
    DetailInstructionsView,
    DetailFooterView,
    DetailMyApplianceView
  },
  props: {
    detail: {
      type: RecipeDetailInfo,
      require: true
    },
    selectThemeType: {
      type: String,
      require: true
    },
    selectVariationType: {
      type: String,
      require: true
    },
    update: {
      type: Boolean,
      require: true
    },
    applianceData: {
      type: ApplianceData,
      require: true
    },
    attachmentList: {
      type: Array as PropType<AttachmentData[]>,
      require: true
    },
    isLogin: {
      type: Boolean,
      require: true
    },
    pageScrollTop: {
      type: Number,
      require: true
    },
    recipeComment: {
      type: RecipeComment,
      require: true
    }
  },
  setup(props, context: SetupContext) {
    NativeClient.sendDebugLog(
      "RecipeDetailView setup " + JSON.stringify(props.detail)
    );
    const PADDING_COMMENT_COUNT = 3;
    const PADDING_PADDING_REM = 2;
    const PADDING_PADDING_ITEM_REM = 14;
    const instructionsView = ref<ComponentPublicInstance<HTMLElement>>();
    const emptyView = ref<ComponentPublicInstance<HTMLElement>>();
    const isShowRecipeDetail = () => {
      return props.detail?.info;
    };
    /**
     * レシピ画像を取得する
     */
    const getDetailImage = () => {
      if (
        props.detail &&
        props.detail.info &&
        props.detail.info.images.length != 0
      ) {
        const images = props.detail.info.images[0];
        if (images == null) {
          return "";
        }
        if (images.main != null) {
          return images.main;
        } else if (images.thumb != null) {
          return images.thumb;
        }
      }
      return "";
    };

    const getVideoUrl = () => {
      return props.detail && props.detail.info && props.detail.info.video != null ? props.detail.info.video : "";
    };

    const isShowVideo = () => {
      return props.detail && props.detail.info && props.detail.info.video != null && props.detail.info.video != "";
    }

    const getSubTitle = () => {
      return props.detail && props.detail.info && props.detail.info.subTitle
        ? props.detail.info.subTitle
        : "";
    };

    const getTitle = () => {
      return props.detail && props.detail.info && props.detail.info.title
        ? props.detail.info.title
        : "";
    };

    const getLabel = () => {
      return props.detail && props.detail.info && props.detail.info.titleLabel
        ? props.detail.info.titleLabel.description
        : null;
    };

    const getLabelColor = () => {
      return props.detail && props.detail.info && props.detail.info.titleLabel
        ? props.detail.info.titleLabel.fgColor
        : null;
    };

    const getLabelBackgroundColor = () => {
      return props.detail && props.detail.info && props.detail.info.titleLabel
        ? props.detail.info.titleLabel.bgColor
        : null;
    };

    const getContent = () => {
      return props.detail && props.detail.info
        ? props.detail.info.content
        : null;
    };

    const getInstructions = () => {
      const items = Array<InstructionItem>();
      if (
        props.detail &&
        props.detail.info &&
        props.detail.info.content &&
        props.detail.info.content.instructions
      ) {
        let i = 0;
        for (const instruction of props.detail.info.content.instructions) {
          const lastItem =
            i == props.detail.info.content.instructions.length - 1;
          items.push(new InstructionItem(i++, instruction, lastItem));
        }
      }
      return items;
    };

    const getTitleNote = () => {
      return props.detail &&
        props.detail.info &&
        props.detail.info.notes &&
        props.detail.info.notes.length >= 1
        ? props.detail.info.notes[0]
        : null;
    };

    const getFooterNote = () => {
      return props.detail &&
        props.detail.info &&
        props.detail.info.notes &&
        props.detail.info.notes.length >= 2
        ? props.detail.info.notes[1]
        : null;
    };

    const getApplianceCategory = () => {
      return props.detail && props.detail.info
        ? props.detail.info.applianceCategory
        : null;
    };

    const getApplianceCategoryId = () => {
      return props.detail && props.detail.info
        ? props.detail.info.applianceCategoryId
        : -1;
    };

    const getNutrition = () => {
      return props.detail && props.detail.info
        ? props.detail.info.nutrition
        : null;
    };

    const getReactions = () => {
      return props.detail && props.detail.info
        ? props.detail.info.reactions
        : null;
    };

    const getThemeList = () => {
      let result = null;
      if (props.detail && props.detail.info) {
        if (props.detail.info.themes && props.detail.info.themes.length >= 1) {
          result = props.detail.info.themes;
        } else if (props.detail.info.content && props.detail.info.content.theme) {
          const result = new Array<Theme>();
          result.push(props.detail.info.content.theme);
        }
      }
      NativeClient.sendDebugLog("RecipeDetailView getThemeList result=" + JSON.stringify(result));
      return result;
    };

    const getVariationList = () => {
      let result = null;
      if (props.detail && props.detail.info) {
        if (props.detail.info.variation && props.detail.info.variation.length >= 1) {
          result = props.detail.info.variation;
        } else if (props.detail.info.content && props.detail.info.content.servings) {
          result = new Array<Variation>();
          result.push(props.detail.info.content.servings);
        }
      }
      NativeClient.sendDebugLog("RecipeDetailView getVariationList result=" + JSON.stringify(result));
      return result;
    };

    const getSelectThemeType = () => {
      return props.selectThemeType;
    };

    const getSelectVariationType = () => {
      return props.selectVariationType;
    };

    const clickTheme = (type: string) => {
      context.emit("click-theme", type);
    };

    const clickVariation = (type: string) => {
      context.emit("click-variation", type);
    };

    const setInstructionItemPosition = (id: number, rect: DOMRect, pageScrollTop: number) => {
      context.emit("set-instruction-item-position", id, rect, pageScrollTop);
    };

    const clickBistroSend = (dlMenuId: number) => {
      context.emit("click-bistro-send", dlMenuId);
    };

    const clickBistroSendLink = (type: string) => {
      context.emit("click-bistro-send-link", type);
    };

    const updateDetail = computed(async () => {
      NativeClient.sendDebugLog(
        "RecipeDetailView props.update=" + props.update
      );
      if (props.update) {
        await nextTick(() => {
          const emptyRect = emptyView.value?.getBoundingClientRect();
          NativeClient.sendDebugLog(
            "RecipeDetailView updateDetail nextTick emptyRect=" +
              JSON.stringify(emptyRect) + " pageScrollTop = " + props.pageScrollTop
          );
          const bottom = emptyRect ? emptyRect.bottom : -1;
          context.emit("set-detail-view-bottom-position", bottom, props.pageScrollTop);
        });
      }
    });

    const getRecipeAttatchmentList = () => {
      let list = Array<Attachment>();
      if (props.detail && props.detail.info && props.detail.info.attachments) {
        list = props.detail.info.attachments;
      }
      return list;
    };

    const isShowMyAppliance = () => {
      let result = false;
      if (props.detail && props.detail.info && props.detail.info.applianceCategoryId && !props.detail.info.myApplianceRecipe) {
        if (props.detail.info.applianceCategoryId && props.detail.info.applianceCategoryId != null) {
          const applianceCategoryId = props.detail.info.applianceCategoryId;
          if (applianceCategoryId == SEARCH_APPLIANCE_CATEGORY_ID.MICRO_WAVE_OVEN
              || applianceCategoryId == SEARCH_APPLIANCE_CATEGORY_ID.IH_COOKING_HEATER
              || applianceCategoryId == SEARCH_APPLIANCE_CATEGORY_ID.RICE_COOKER
              || applianceCategoryId == SEARCH_APPLIANCE_CATEGORY_ID.AUTO_COKKER) {
              result = true;
          }
        }
      }
      return result;
    };

    const clickMyApplianceLink = () => {
      context.emit("click-my-appliance-link");
    };

    const getRecipeId = () => {
      return props.detail && props.detail.info
        ? props.detail.info.recipeId
        : "";
    };

    const getRecipeType = () => {
      return props.detail && props.detail.info ? props.detail.info.type : "";
    };

    const getPaddingBottom = () => {
      const size = props.recipeComment != null && props.recipeComment.comments != null ? props.recipeComment.comments.length : 0;
      const paddingCount = PADDING_COMMENT_COUNT - size;
      const padding = PADDING_PADDING_REM + paddingCount * PADDING_PADDING_ITEM_REM;
      NativeClient.sendDebugLog(
        "RecipeDetailView getPaddingBottom size=" + size + " padding=" + padding
      );
      return padding + "rem";
    };

    return {
      isShowRecipeDetail,
      getDetailImage,
      getVideoUrl,
      isShowVideo,
      getSubTitle,
      getTitle,
      getLabel,
      getLabelColor,
      getLabelBackgroundColor,
      getContent,
      getInstructions,
      getTitleNote,
      getFooterNote,
      getApplianceCategory,
      getApplianceCategoryId,
      getNutrition,
      getReactions,
      getThemeList,
      getVariationList,
      getSelectThemeType,
      getSelectVariationType,
      clickTheme,
      clickVariation,
      instructionsView,
      updateDetail,
      setInstructionItemPosition,
      clickBistroSend,
      getRecipeAttatchmentList,
      clickBistroSendLink,
      clickMyApplianceLink,
      isShowMyAppliance,
      emptyView,
      getRecipeId,
      getRecipeType,
      getPaddingBottom
    };
  }
});
